// Mutation cache-strategy taxonomy
//
// Every mutation in this file uses one or more of the strategies below
// (strategies may compose, e.g. plain-invalidate of several related keys).
//
// 1. Optimistic update (onMutate + onError rollback) followed by onSettled
//    invalidate: for update mutations needing immediate UI feedback without a
//    round-trip, then revalidating from the server once the call settles.
//    Used by useUpdateTopicMutation (onMutate snapshots and patches the topic
//    cache, onError restores the snapshot, onSettled invalidates the topic
//    query). This variant seeds no cache from the server response; it relies
//    on the follow-up invalidate instead of onSuccess setQueryData.
// 2. Plain invalidate (onSuccess invalidateQueries only): for any mutation
//    where the server is the source of truth (create, delete, flush). Used by
//    useCreateTopicMutation, useDeleteTopicMutation, and useFlushTopicMutation.
//    useFlushTopicMutation composes several plain invalidates (topic,
//    topicMessages, and the topics list) to refresh counts and the message list.

// 1. React and DOM imports

// 2. External libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';

// 3. Internal components

// 4. Type imports
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  CreateTopicRequest,
  FlushTopicResponse,
  TopicResponse,
  UpdateTopicRequest,
} from '~/generated/api/model';

// 5. Custom hooks and utilities
import {
  createTopicV1TopicsPost,
  deleteTopicV1TopicsTopicIdDelete,
  flushTopicV1TopicsTopicIdFlushPost,
  updateTopicV1TopicsTopicIdPatch,
} from '~/generated/api/topics/topics';
import { queryKeys } from './queryKeys';

interface UpdateTopicContext {
  previousTopic: TopicResponse | null;
}

export function useUpdateTopicMutation(
  topicId: string
): UseMutationResult<TopicResponse, Error, UpdateTopicRequest, UpdateTopicContext> {
  const queryClient = useQueryClient();

  return useMutation<TopicResponse, Error, UpdateTopicRequest, UpdateTopicContext>({
    mutationFn: async (data: UpdateTopicRequest) => {
      return updateTopicV1TopicsTopicIdPatch(topicId, data);
    },
    // Optimistic update: immediately update cache before API responds
    onMutate: async (updatedData) => {
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: queryKeys.topic(topicId) });

      // Snapshot current value for rollback
      const previousTopic =
        queryClient.getQueryData<TopicResponse>(queryKeys.topic(topicId)) ?? null;

      // Optimistically update cache
      queryClient.setQueryData<TopicResponse>(queryKeys.topic(topicId), (old) => {
        if (!old) return old;
        return {
          ...old,
          name: updatedData.name ?? old.name,
          description: updatedData.description ?? old.description,
          // Cast is safe: UpdateTopicRequestOverflowBehavior and TopicResponseOverflowBehavior
          // have identical values
          overflowBehavior: updatedData.overflowBehavior ?? old.overflowBehavior,
        };
      });

      return { previousTopic };
    },
    // Rollback on error
    onError: (_err, _updatedData, context) => {
      if (context?.previousTopic) {
        queryClient.setQueryData(queryKeys.topic(topicId), context.previousTopic);
      }
    },
    // Revalidate after mutation completes
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.topic(topicId) });
    },
  });
}

export function useDeleteTopicMutation(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string, unknown>({
    mutationFn: async (topicId: string) => {
      return deleteTopicV1TopicsTopicIdDelete(topicId);
    },
    onSuccess: () => {
      // Invalidate topics list to refetch without deleted topic
      void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
    },
  });
}

export function useFlushTopicMutation(
  topicId: string
): UseMutationResult<FlushTopicResponse, Error, void, unknown> {
  const queryClient = useQueryClient();

  return useMutation<FlushTopicResponse, Error, void, unknown>({
    mutationFn: async () => {
      return flushTopicV1TopicsTopicIdFlushPost(topicId);
    },
    onSuccess: () => {
      // Invalidate topic query to update message count to 0
      void queryClient.invalidateQueries({ queryKey: queryKeys.topic(topicId) });
      // Invalidate topic messages query to clear messages list (matches useTopicMessagesInfiniteQuery key)
      void queryClient.invalidateQueries({ queryKey: queryKeys.topicMessages(topicId) });
      // Invalidate topics list to update aggregate counts
      void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
    },
  });
}

export function useCreateTopicMutation(): UseMutationResult<
  TopicResponse,
  Error,
  CreateTopicRequest,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation<TopicResponse, Error, CreateTopicRequest, unknown>({
    mutationFn: async (data: CreateTopicRequest) => {
      return createTopicV1TopicsPost(data);
    },
    onSuccess: () => {
      // Invalidate topics list to refetch with new topic
      void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
    },
  });
}
